'use client';
import React, { useState } from 'react';
import { ChevronRight, AlertCircle } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';

// Backend integration point: replace with API call to /api/compliance/top-delinquents
const delinquents = [
  { id: 'dq-001', tin: 'LR-0041820', name: 'Nimely Resources Ltd', sector: 'Mining', amount: 4.82, days: 312, status: 'overdue' as const },
  { id: 'dq-002', tin: 'LR-0029341', name: 'Pewee Transport Corp', sector: 'Logistics', amount: 3.17, days: 198, status: 'overdue' as const },
  { id: 'dq-003', tin: 'LR-0071205', name: 'Kollie Properties', sector: 'Real Estate', amount: 2.94, days: 445, status: 'overdue' as const },
  { id: 'dq-004', tin: 'LR-0083910', name: 'Gbowee Agro Holdings', sector: 'Agriculture', amount: 1.76, days: 87, status: 'pending' as const },
  { id: 'dq-005', tin: 'LR-0015672', name: 'Jallah Telecom Ltd', sector: 'Telecoms', amount: 1.43, days: 156, status: 'overdue' as const },
  { id: 'dq-006', tin: 'LR-0096234', name: 'Sirleaf Pharma Inc', sector: 'Healthcare', amount: 0.98, days: 63, status: 'pending' as const },
  { id: 'dq-007', tin: 'LR-0034811', name: 'Weah Coastal Fisheries', sector: 'Fishing', amount: 0.74, days: 201, status: 'overdue' as const },
];

export default function TopDelinquentsTable() {
  const [hovered, setHovered] = useState<string | null>(null);

  return (
    <div
      className="rounded-xl p-5 h-full flex flex-col"
      style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold" style={{ color: 'var(--dark-text)' }}>
            Top Delinquent Accounts
          </h3>
          <p className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>
            Highest outstanding balances · Enforcement priority
          </p>
        </div>
        <AlertCircle size={16} style={{ color: '#fb923c' }} />
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-thin">
        <table className="w-full text-xs">
          <thead>
            <tr style={{ borderBottom: '1px solid var(--dark-border)' }}>
              {['Taxpayer', 'Sector', 'Owed', 'Days', 'Status'].map((h) => (
                <th
                  key={`dq-th-${h}`}
                  className="text-left py-2 px-1 font-semibold uppercase tracking-wide"
                  style={{ color: 'var(--dark-muted)', fontSize: '10px', letterSpacing: '0.06em' }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {delinquents.map((row) => (
              <tr
                key={row.id}
                onMouseEnter={() => setHovered(row.id)}
                onMouseLeave={() => setHovered(null)}
                className="transition-colors duration-100 cursor-pointer"
                style={{
                  backgroundColor: hovered === row.id ? 'rgba(255,255,255,0.04)' : 'transparent',
                  borderBottom: '1px solid rgba(255,255,255,0.04)',
                }}
              >
                <td className="py-2 px-1">
                  <div>
                    <p className="font-medium truncate max-w-[90px]" style={{ color: 'var(--dark-text)' }}>
                      {row.name}
                    </p>
                    <p className="font-mono-data" style={{ color: 'var(--dark-muted)', fontSize: '10px' }}>
                      {row.tin}
                    </p>
                  </div>
                </td>
                <td className="py-2 px-1" style={{ color: 'var(--dark-muted)' }}>
                  {row.sector}
                </td>
                <td className="py-2 px-1 tabular-nums font-semibold" style={{ color: '#fca5a5' }}>
                  ${row.amount}M
                </td>
                <td className="py-2 px-1 tabular-nums" style={{ color: row.days > 180 ? '#fca5a5' : 'var(--dark-muted)' }}>
                  {row.days}d
                </td>
                <td className="py-2 px-1">
                  <StatusBadge variant={row.status} size="sm" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <button
        className="mt-3 w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all duration-150 hover:bg-white/10"
        style={{ color: 'var(--accent)', border: '1px solid rgba(212,160,23,0.2)' }}
      >
        View All 8,342 Delinquent Accounts
        <ChevronRight size={13} />
      </button>
    </div>
  );
}